import React from 'react';
import {Link} from 'react-router-dom';
import {withI18n, WithI18nProps} from '@shopify/react-i18n';
import {Card, Icon, TextStyle} from '@shopify/polaris';
import {Address, Money} from 'sections/types';
import {ProfileImage, ServiceMetrics, Heading} from '..';
import {LocationMajorMonotone} from '@shopify/polaris-icons';
import {formatCurrency} from 'utilities/numbers';
import i18nArgs from './locales';

import styles from './ExpertCard.scss';

interface OwnProps {
  profileImage: string;
  businessName: string;
  address: Address;
  startingPrice: Money;
  jobsCompleted: number;
  overallRating?: number | null;
  totalReviews: number;
  link: string;
  external?: boolean;
}

type CombinedProps = OwnProps & WithI18nProps;

function ExpertCard({
  profileImage,
  businessName,
  address,
  startingPrice,
  jobsCompleted,
  overallRating,
  totalReviews,
  link,
  external,
  i18n,
}: CombinedProps) {
  const Address = (
    <div className={styles.Address}>
      <Icon source={LocationMajorMonotone} color="inkLightest" />
      <TextStyle variation="subdued">
        {address.city}, {address.country}
      </TextStyle>
    </div>
  );

  const CardHeader = (
    <div className={styles.Header} role="group">
      <div className={styles.ProfileImageContainer}>
        <ProfileImage source={profileImage} />
      </div>
      <div className={styles.NameAndAddressContainer}>
        <Heading>{businessName}</Heading>
        {Address}
      </div>
    </div>
  );

  const CardMetrics = (
    <div className={styles.ServiceMetricsContainer}>
      <ServiceMetrics
        price={startingPrice}
        jobsCompleted={jobsCompleted && jobsCompleted}
        overallRating={overallRating && overallRating}
        totalReviews={totalReviews && totalReviews}
        variation="ExpertCard"
      />
    </div>
  );

  const ariaLabel: any = i18n.translate('ExpertCard.linkLabel', {
    businessName,
    city: address.city,
    country: address.country,
    startingPrice: formatCurrency(
      startingPrice.amount,
      0,
      startingPrice.currencyCode
    ),
  });

  const CardMarkup = (
    <Card>
      <div className={styles.CardContent}>
        {CardHeader}
        {CardMetrics}
      </div>
    </Card>
  );

  return (
    <div className={styles.ExpertCard}>
      {external ? (
        <a
          href={link}
          aria-label={ariaLabel}
          rel="noopener noreferrer"
          target="_blank"
        >
          {CardMarkup}
        </a>
      ) : (
        <Link to={link} aria-label={ariaLabel}>
          {CardMarkup}
        </Link>
      )}
    </div>
  );
}

export default withI18n(i18nArgs)(ExpertCard);
